import {Injectable} from '@angular/core';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {AngularFireDatabase, AngularFireObject} from '@angular/fire/compat/database';
import { MatIconRegistry } from '@angular/material/icon';
import {dbPaths} from "../common/db";
import {Texte} from "../common/texte";
import {betragToString} from "../common/utils";
import {SentryService} from "../utils/sentry.service";
import {
  Capacity,
  EinzelBuchung,
  EinzelBuchungJson,
  Reise,
  Reisen
} from './types';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../environments/environment';
import {DomSanitizer} from '@angular/platform-browser';
import {User} from './user';
import {DialogService} from '../utils/dialog.service';
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject, combineLatest, first, Observable, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import * as moment from "moment";
import {GruppenBuchung} from "../buchung/buchung";


// TODO rename BuchungService
@Injectable()
export class FirebaseService {
  reisen = new BehaviorSubject<Reisen>(null);
  texte = new Texte(environment);
  user = new BehaviorSubject<User>(null);
  userIsAdmin = new BehaviorSubject<boolean>(false);
  profilePicStyles: {};
  userAuthProviderIcon: string;
  private publicDbObject: AngularFireObject<any>;
  private userDataSubscription: Subscription;
  private buchungenSubscription: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    public dialog: DialogService,
    private http: HttpClient,
    private router: Router,
    private db: AngularFireDatabase,
    private auth: AngularFireAuth,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private sentryService: SentryService
  ) {
    this.publicDbObject = this.db.object('/public');
    this.publicDbObject.valueChanges().subscribe(publicData => {
      this.reisen.next(new Reisen(publicData));
    });

    this.auth.authState.subscribe(firebaseUser => {
      if (firebaseUser) {
        this.user.next(this.createUser(firebaseUser.uid));
        firebaseUser.getIdTokenResult().then((idTokenResult) => {
          this.userIsAdmin.next(!!idTokenResult.claims.admin);
        });
        const provider = firebaseUser.providerData[0]?.providerId;
        if (provider?.startsWith('google')) {
          this.userAuthProviderIcon = 'auth_service_google';
        } else if (provider?.startsWith('facebook')) {
          this.userAuthProviderIcon = 'auth_service_facebook';
        } else if (provider?.startsWith('password')) {
          this.userAuthProviderIcon = 'auth_service_email';
        } else {
          console.warn('unexpected auth provider:', firebaseUser.providerData[0]);
          this.userAuthProviderIcon = null;
        }
        const avatar = firebaseUser.photoURL ? firebaseUser.photoURL : `assets/${this.userAuthProviderIcon}.svg`;
        this.profilePicStyles = {
          'background-image': `url(${avatar})`
        };
      } else {
        if (this.userDataSubscription) {
          this.userDataSubscription.unsubscribe();
        }
        if (this.buchungenSubscription) {
          this.buchungenSubscription.unsubscribe();
        }
        this.router.navigate(['/']);
        this.user.next(null);
        this.userIsAdmin.next(false);
        this.profilePicStyles = {
          'background-image': null
        };
        this.userAuthProviderIcon = null;
      }
    });
    this.createAuthServiceIcons();
  }

  get env() {
    return environment;
  }

  /*
  signInGoogle() {
    const provider = new firebase.auth.GoogleAuthProvider();
    this.auth.signInWithPopup(provider).then(user => {
      this.sentryService.logSignIn('signInGoogle', user);
      this.dialog.openSnackBar('Erfolgreich angemeldet.');
    }).catch(error => {
      console.error(error);
      this.dialog.openSnackBar('Fehler beim Anmelden: ' + error.message);
    });
    // TODO Handling account-exists-with-different-credential Errors, see https://firebase.google.com/docs/auth/web/facebook-login
  }
  
  signInFacebook() {
    const provider = new firebase.auth.FacebookAuthProvider();
    provider.addScope('email');
    this.auth.signInWithPopup(provider).then(user => {
      this.sentryService.logSignIn('signInFacebook', user);
      this.dialog.openSnackBar('Erfolgreich angemeldet.');
    }).catch(error => {
      console.error(error);
      this.dialog.openSnackBar('Fehler beim Anmelden: ' + error.message);
    });
  }
  
  // Registrieren/Login
  signInEmailCreateAccount(email, password) {
    this.auth.createUserWithEmailAndPassword(email, password)
      .catch(error => {
        const errorCode = error.code;
        const errorMessage = error.message;
        if (errorCode === 'auth/weak-password') {
          alert('The password is too weak.');
        } else {
          alert(errorMessage);
        }
        console.log(error);
      });
  }
  
  signInEmail(email, password) {
    this.auth.signInWithEmailAndPassword(email, password)
      .then(user => {
        this.sentryService.logSignIn('signInEmail', user);
        this.dialog.openSnackBar('Erfolgreich angemeldet.');
      })
      .catch(error => {
        const errorCode = error.code;
        const errorMessage = error.message;
        if (errorCode === 'auth/wrong-password') {
          alert('Wrong password.');
        } else {
          alert(errorMessage);
        }
        console.log(error);
      });
  }
   */
  
  async signOut() {
    await this.sentryService.logSignOut();
    this.user.next(null);
    this.userIsAdmin.next(false);
    this.auth.signOut().then(() => {
      this.dialog.openSnackBar('Erfolgreich abgemeldet.');
    });
  }
  
  getIdToken(): Promise<string> {
    return this.auth.idToken.pipe(first()).toPromise();
  }

  storeStammdaten(user: User) {
    this.db.object(dbPaths.stammdaten(user.uid)).set(user.toJson());
  }

  getAlleReisen(): Observable<Reise[]> {
    return this.reisen.pipe(map(reisen => reisen ? reisen.reisenList.filter(reise => !reise.archiv) : null));
  }
  getBuchbareReisen(): Observable<Reise[]> {
    return this.reisen.pipe(map(reisen => reisen ? reisen.reisenList.filter(reise => !reise.archiv && reise.buchbar) : null));
  }
  storeCapacity(capacity: Capacity) {
    // Cache Werte nicht überschreiben
    this.db.object(dbPaths.capacity(capacity.$key)).update(capacity.constValues());
  }

  addUserAnfrage(buchung: GruppenBuchung): Promise<any> {
    return this.postToCloudFunction('createAnfrage', buchung.toJson(),
      'Anfrage', 'Herzlichen Dank für Deine Buchungsanfrage! Eine Bestätigung folgt per E-Mail...');
  }

  buchungAendern(buchung: EinzelBuchung) {
    if (buchung.sonderpreis < 0 || buchung.sonderpreis > 999) {
      this.dialog.openSnackBar(`Sonderpreis ungültig: ${buchung.sonderpreis}`);
      buchung.sonderpreis = null;
      return;
    }
    const buchungJson = buchung.toJson() as EinzelBuchungJson;
    buchungJson.$key = buchung.$key;
    this.buchungAendernJson(buchungJson);
  }

  buchungAendernJson(buchungJson: EinzelBuchungJson) {
    this.postToCloudFunction('buchungAendern', buchungJson, 'Änderung');
  }

  buchungStornieren(buchung: EinzelBuchung) {
    const body = `Reise: ${buchung.reise.name}<br>
Teilnehmer: ${buchung.teilnehmer.name()}<br>
Kategorie: ${buchung.kategorie.name}<br><br>
Stornokosten: ${betragToString(buchung.getStornoKosten(new Date()))}`;
    this.dialog.openYesNoDialog('Buchung stornieren?', body).subscribe(doIt => {
      if (!doIt) {
        return;
      }
      this.postToCloudFunction('buchungStornieren', {buchungKey: buchung.$key}, 'Storno');
    });
  }

  buchungAbsagen(buchung: EinzelBuchung) {
    const body = `Reise: ${buchung.reise.name}<br>
Teilnehmer: ${buchung.teilnehmer.name()}<br>
Kategorie: ${buchung.kategorie.name}`;
    this.dialog.openYesNoDialog('Reise absagen?', body).subscribe(doIt => {
      if (!doIt) {
        return;
      }
      this.postToCloudFunction('buchungAbsagen', {buchungKey: buchung.$key}, 'Reise absagen');
    });
  }

  buchungLoeschen(buchung: EinzelBuchung) {
    const body = `Reise: ${buchung.reise.name}<br>
Teilnehmer: ${buchung.teilnehmer.name()}<br>
Kategorie: ${buchung.kategorie.name}<br><br>
kein Kosten beim Löschen`;
    this.dialog.openYesNoDialog('Buchung löschen?', body).subscribe(doIt => {
      if (!doIt) {
        return;
      }
      this.postToCloudFunction('buchungLoeschen', {buchungKey: buchung.$key}, 'Löschen');
    });
  }

  postToCloudFunction(functionName: string, body, dialogTitle?: string, dialogBody?: string): Promise<any> {
    const dialog = dialogTitle ? this.dialog.openProgressDialog(dialogTitle, dialogBody) : null;
    return this.getIdToken().then(token => {
      const options = {
        headers: new HttpHeaders().set('Authorization', 'Bearer ' + token),
      };
      return this.http.post(`${environment.functionsURL}/${functionName}`, body, options).toPromise()
        .then(data => {
          if (dialog) {
            this.dialog.closeProgressDialogSuccess(dialog);
          }
          return data;
        })
        .catch(err => {
          if (dialog) {
            this.dialog.closeProgressDialogError(dialog);
          }
          throw err;
        });
    });
  }

  private createUser(uid: string): User {
    const user = new User(uid);
    if (this.userDataSubscription) {
      this.userDataSubscription.unsubscribe();
    }
    this.userDataSubscription = combineLatest(
      this.reisen,
      this.db.object(dbPaths.user(user.uid)).valueChanges(),
      this.activatedRoute.queryParamMap
    ).subscribe(v => {
      if (this.buchungenSubscription) {
        this.buchungenSubscription.unsubscribe();
        this.buchungenSubscription = null;
      }
      const [reisen, userData, queryParamMap] = v;
      if (!userData) {
        return;
      }
      const archiv = queryParamMap.get('archiv') === '1';
      user.setUserData(userData, archiv);
      const buchungenKeysObj = userData['buchungen'];
      const buchungenKeysList = buchungenKeysObj ? Object.keys(buchungenKeysObj) : [];
      this.buchungenSubscription = combineLatest(buchungenKeysList
        .map(key => this.db.object(dbPaths.buchung(key)).valueChanges()
          .pipe(map((buchungJson: EinzelBuchungJson) => {
            if (buchungJson) {
              buchungJson.$key = key;
            }
            return buchungJson;
          }))))
        .subscribe(buchungenJson => user.setBuchungen(reisen, buchungenJson, archiv));
    });
    return user;
  }

  private createAuthServiceIcons() {
    const icons = ['auth_service_email', 'auth_service_facebook', 'auth_service_google'];
    icons.forEach(icon =>
      this.iconRegistry.addSvgIcon(icon, this.sanitizer.bypassSecurityTrustResourceUrl(`assets/${icon}.svg`)));
  }
}
